/**
 * Slide Primitives - Shared Styled Components
 * Reusable building blocks for pitch deck slides
 */

import styled from 'styled-components';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing, borderRadius } from '@config/spacing';
import { media } from '@config/breakpoints';

// ============================================
// Slide Base
// ============================================

export const SlideBase = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  overflow-y: auto;
  position: relative;
  padding: ${spacing.md} ${spacing.lg};
  gap: ${spacing.md};

  /* Auto spacers: centre content when it fits, collapse when it overflows */
  &::before,
  &::after {
    content: '';
    flex: 1 0 0px;
  }

  ${media.md} {
    padding: ${spacing.lg} ${spacing['2xl']};
  }
`;

/** Vertical gradient divider for side-by-side layouts. */
export const VerticalDivider = styled.div`
  width: 3px;
  align-self: center;
  height: 90%;
  background: linear-gradient(
    180deg,
    transparent 0%,
    ${colors.highlight.extraLight} 20%,
    ${colors.highlight.light} 50%,
    ${colors.highlight.extraLight} 80%,
    transparent 100%
  );
  margin: 0 ${spacing.lg};
  flex-shrink: 0;
  border-radius: 1px;
`;

/** "See more in Appendix" link — bottom-right, above the copyright line. */
export const SeeMoreLink = styled.div`
  align-self: flex-end;
  flex-shrink: 0;
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xs}, 1.1vw, ${typography.fontSize.sm});
  color: ${colors.highlight.primary};
  font-style: italic;
  cursor: pointer;

  &:hover {
    color: ${colors.highlight.dark};
    text-decoration: underline;
  }

`;

/** Vision-scale base with extra-generous padding for hero/title slides. */
export const HeaderSlideBase = styled(SlideBase)`
  padding: ${spacing['3xl']} ${spacing['2xl']};

  ${media.md} {
    padding: ${spacing['4xl']} ${spacing['5xl']};
  }
`;

export const SlideContent = styled.div`
  max-width: 1120px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.xl};
  z-index: 1;
`;

// ============================================
// Typography — matches landing page styling
// ============================================

export const SlideTitle = styled.h1`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize['3xl']}, 3.5vw, ${typography.fontSize['4xl']});
  font-weight: 100;
  background: linear-gradient(135deg, ${colors.highlight.extraDark} 0%, ${colors.highlight.dark} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  text-align: center;
  line-height: ${typography.lineHeight.tight};
`;

export const SlideSubtitle = styled.p`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.base}, 1.8vw, ${typography.fontSize.xl});
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.highlight.dark};
  margin: 0;
  text-align: center;
  line-height: ${typography.lineHeight.relaxed};
`;

/** Hero-scale title for the opening/vision slide. */
export const HeroTitle = styled(SlideTitle)`
  font-size: clamp(${typography.fontSize['3xl']}, 5vw, ${typography.fontSize['5xl']});
  letter-spacing: ${typography.letterSpacing.tight};
`;

/** Hero-scale subtitle for the opening/vision slide. */
export const HeroSubtitle = styled(SlideSubtitle)`
  font-size: clamp(${typography.fontSize.lg}, 2.5vw, ${typography.fontSize['2xl']});
`;

export const SlideSectionTitle = styled.h3`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.lg}, 2vw, ${typography.fontSize.xl});
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.highlight.dark};
  margin: 0;
  text-align: center;
`;

export const SlideNote = styled.div`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.sm}, 1vw, ${typography.fontSize.md});
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.highlight.light};
  text-align: center;
  flex-shrink: 0;
  font-style: italic;
`;

// ============================================
// Numbers & Data
// ============================================

export const BigNumber = styled.div`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize['4xl']}, 8vw, ${typography.fontSize['6xl']});
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.highlight.dark};
  line-height: 1;
  text-align: center;
`;

export const BigNumberLabel = styled.div`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.sm}, 1.5vw, ${typography.fontSize.lg});
  color: ${colors.highlight.primary};
  text-align: center;
  margin-top: ${spacing.sm};
`;

// ============================================
// Text Highlights
// ============================================

// ============================================
// Cards & Segments
// ============================================

export const SegmentCard = styled.div<{ $accent?: string }>`
  flex: 1;
  min-width: 200px;
  padding: ${spacing.xl};
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: ${borderRadius.xl};
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  border-top: 3px solid ${props => props.$accent || colors.brand.primaryDark};
  box-shadow: 0 2px 8px rgba(51, 65, 85, 0.06);
`;

// ============================================
// Timeline
// ============================================

export const TimelineContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`;

export const TimelineLabels = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 ${spacing.sm};
`;

export const TimelineLabel = styled.span`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.highlight.light};
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
`;

export const TimelineTrack = styled.div`
  position: relative;
  width: 100%;
  height: 4px;
  background: rgba(51, 65, 85, 0.1);
  border-radius: ${borderRadius.full};
`;

export const TimelineFill = styled.div`
  position: absolute;
  inset: 0;
  border-radius: ${borderRadius.full};
  background: linear-gradient(
    90deg,
    ${colors.accent.teal.exxtraDark} 0%,
    ${colors.accent.blue.exxtraDark} 50%,
    ${colors.accent.purple.exxtraDark} 100%
  );
  opacity: 0.3;
`;

export const TimelineStops = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  margin-top: -12px;
  padding: 0 4%;
`;

export const TimelineStop = styled.div<{ $color: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  max-width: 33%;
`;

export const TimelineDot = styled.div<{ $color: string }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${props => props.$color};
  border: 3px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  z-index: 1;
`;

export const TimelineStopTitle = styled.div<{ $color: string }>`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.sm}, 1.4vw, ${typography.fontSize.base});
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.$color};
  text-align: center;
  margin-top: ${spacing.sm};
`;

export const TimelineStopDetail = styled.div`
  margin-top: ${spacing.sm};
  text-align: left;
  width: 100%;
  padding: 0 ${spacing.sm};
`;

export const TimelineBullet = styled.li`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xs}, 1.2vw, ${typography.fontSize.sm});
  color: ${colors.highlight.primary};
  line-height: ${typography.lineHeight.relaxed};
  padding-left: ${spacing.lg};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${colors.highlight.light};
  }
`;

export const TimelineBulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

// ============================================
// Team Staffing
// ============================================

// ============================================
// Team / Person
// ============================================

export const PersonPhoto = styled.div<{ $src?: string }>`
  width: clamp(80px, 10vw, 130px);
  height: clamp(80px, 10vw, 130px);
  border-radius: 50%;
  background: ${props => props.$src ? `url(${props.$src}) center/cover` : colors.brand.primary};
  border: 3px solid ${colors.brand.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: ${typography.fontSize['3xl']};
  color: ${colors.highlight.dark};
`;

export const PersonName = styled.h3`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.base}, 1.8vw, ${typography.fontSize.xl});
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.highlight.dark};
  margin: 0;
  text-align: center;
`;

export const PersonTitle = styled.p`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xs}, 1.1vw, ${typography.fontSize.base});
  color: ${colors.brand.exxtraDark};
  margin: 0;
  text-align: center;
`;

export const PersonBio = styled.p`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xxs}, 0.9vw, ${typography.fontSize.sm});
  color: ${colors.highlight.primary};
  margin: 0;
  text-align: center;
  max-width: 380px;
  line-height: ${typography.lineHeight.relaxed};
`;

// ============================================
// Diagram / Flow
// ============================================

// ============================================
// Bullet Lists
// ============================================

// ============================================
// Beachhead / Funnel
// ============================================

// ============================================
// Pulsing Icon
// ============================================

// ============================================
// Slide Background Accents
// ============================================

export const SlideAccentGlow = styled.div`
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(216, 243, 237, 0.4) 0%, transparent 70%);
  pointer-events: none;
  top: -100px;
  right: -100px;
`;

export const SlideAccentGlowBottom = styled(SlideAccentGlow)`
  top: auto;
  right: auto;
  bottom: -100px;
  left: -100px;
  background: radial-gradient(circle, rgba(184, 230, 219, 0.3) 0%, transparent 70%);
`;

// ============================================
// Layout Helpers
// ============================================

// ============================================
// Dark Accent Box
// Reusable dark-themed container with inset brand accent line.
// Use for vision statements, alert callouts, and any dark highlight box.
// ============================================

export const DarkAccentBox = styled.div<{ $accent?: string }>`
  position: relative;
  background: linear-gradient(135deg, ${colors.highlight.dark} 0%, ${colors.highlight.light} 100%);
  border: 1px solid ${(p) => p.$accent || colors.brand.primaryDark};
  border-radius: ${borderRadius['2xl']};
  padding: ${spacing.xl} ${spacing['2xl']};
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);

  /* Inset accent line — brand light border slightly inside the outer edge */
  &::after {
    content: '';
    position: absolute;
    inset: 5px;
    border: 1px solid ${(p) => p.$accent ? `${p.$accent}40` : `${colors.brand.extraLight}40`};
    border-radius: calc(${borderRadius['2xl']} - 4px);
    pointer-events: none;
  }

  ${media.md} {
    padding: ${spacing['2xl']} ${spacing['3xl']};

    &::after {
      inset: 7px;
      border-radius: calc(${borderRadius['2xl']} - 6px);
    }
  }
`;

// ============================================
// Appendix Layout
// Compact slide shell for data-dense appendix slides.
// ============================================

export const AppendixSlideLayout = styled(SlideBase)`
  align-items: stretch;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};

  ${media.md} {
    padding: ${spacing.md} ${spacing.xl};
  }
`;

export const AppendixContentArea = styled.div`
  display: flex;
  gap: ${spacing.md};

  ${media.md} {
    gap: ${spacing.lg};
  }
`;

export const AppendixColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  overflow-y: auto;
`;

export const AppendixSectionLabel = styled.div`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.xxs}, 1vw, ${typography.fontSize.xs});
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.highlight.light};
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

export const AppendixSourceFooter = styled.div`
  flex-shrink: 0;
  font-family: ${typography.fontFamily.body};
  font-size: clamp(7px, 0.65vw, ${typography.fontSize.xxs});
  color: ${colors.highlight.extraLight};
  text-align: center;
  margin-top: ${spacing.xs};
  line-height: 1.5;
`;

// ============================================
// Appendix Stat Bars
// Colored highlight bars for statistics with number + description.
// Pass $accent / $bg to set the theme color; defaults to coral.
// ============================================

export const AppendixStatBar = styled.div<{ $accent?: string; $bg?: string }>`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.xs} ${spacing.md};
  background: ${(p) => p.$bg || `${colors.accent.coral.exxtraLight}30`};
  border-left: 3px solid ${(p) => p.$accent || colors.accent.coral.exxtraDark};
  border-radius: ${borderRadius.sm};
`;

export const AppendixStatNumber = styled.div<{ $color?: string }>`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.base}, 2vw, ${typography.fontSize.lg});
  font-weight: ${typography.fontWeight.bold};
  color: ${(p) => p.$color || colors.accent.coral.exxtraDark};
  flex-shrink: 0;
  min-width: 36px;
  text-align: right;
`;

export const AppendixStatDescription = styled.div`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xxs}, 0.9vw, ${typography.fontSize.sm});
  color: ${colors.highlight.primary};
  line-height: 1.35;
`;

export const AppendixStatSource = styled.span`
  color: ${colors.highlight.extraLight};
  font-style: italic;
`;

// ============================================
// Dark Insight Box
// Compact variant of DarkAccentBox for appendix-density slides.
// Used for "Key Insight", "AI Unlock", "Mental Health Toll" boxes.
// ============================================

export const DarkInsightBox = styled.div`
  padding: ${spacing.sm} ${spacing.md};
  background: linear-gradient(135deg, ${colors.highlight.extraDark} 0%, ${colors.highlight.primary} 100%);
  border: 1px solid ${colors.brand.primaryDark};
  border-radius: ${borderRadius.md};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 3px;
    border: 1px solid ${colors.brand.extraLight}40;
    border-radius: calc(${borderRadius.md} - 2px);
    pointer-events: none;
  }
`;

export const DarkInsightTitle = styled.div<{ $color?: string }>`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.xxs}, 1vw, ${typography.fontSize.xs});
  font-weight: ${typography.fontWeight.semibold};
  color: ${(p) => p.$color || colors.brand.primary};
  margin-bottom: 2px;
`;

export const DarkInsightBody = styled.div`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xxs}, 0.9vw, ${typography.fontSize.sm});
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.4;
`;

export const DarkInsightEmphasis = styled.span`
  color: ${colors.brand.primary};
  font-weight: ${typography.fontWeight.semibold};
`;

// ============================================
// Accent Callout Box
// Light-background callout with colored left border.
// Used for comparisons, quotes, decisions, and generic callouts.
// ============================================

export const AccentCalloutBox = styled.div<{ $accent?: string; $bg?: string }>`
  padding: ${spacing.sm} ${spacing.md};
  background: ${(p) => p.$bg || colors.accent.blue.extraLight};
  border-left: 3px solid ${(p) => p.$accent || colors.accent.blue.exxtraDark};
  border-radius: ${borderRadius.sm};
`;

export const AccentCalloutTitle = styled.div<{ $color?: string }>`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.xxs}, 1vw, ${typography.fontSize.xs});
  font-weight: ${typography.fontWeight.semibold};
  color: ${(p) => p.$color || colors.accent.blue.exxtraDark};
  margin-bottom: 2px;
`;

export const AccentCalloutBody = styled.div`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xxs}, 0.9vw, ${typography.fontSize.sm});
  color: ${colors.highlight.primary};
  line-height: 1.4;
`;

export const AccentCalloutBold = styled.span<{ $color?: string }>`
  font-weight: ${typography.fontWeight.bold};
  color: ${(p) => p.$color || colors.accent.blue.exxtraDark};
`;

// ============================================
// Accent Bordered Card
// Glass card with left-border accent stripe.
// Used for reason cards, compact info cards.
// ============================================

export const AccentBorderedCard = styled.div<{ $accent: string }>`
  padding: ${spacing.sm} ${spacing.md};
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid ${colors.neutral.extraLight};
  border-left: 3px solid ${(p) => p.$accent};
  border-radius: ${borderRadius.sm};
`;

export const AccentBorderedCardTitle = styled.div`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.xxs}, 1vw, ${typography.fontSize.xs});
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.highlight.dark};
  margin-bottom: 1px;
`;

export const AccentBorderedCardBody = styled.div`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xxs}, 0.9vw, ${typography.fontSize.sm});
  color: ${colors.highlight.primary};
  line-height: 1.4;
`;

// ============================================
// Appendix Data Table
// Grid-based data table for comparison and domain tables.
// Pass $columns to customise grid-template-columns.
// ============================================

export const AppendixDataTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

export const AppendixDataRow = styled.div<{ $columns?: string }>`
  display: grid;
  grid-template-columns: ${(p) => p.$columns || '1.4fr 1fr 1fr'};
  gap: ${spacing.xs};
  padding: ${spacing.xs} ${spacing.sm};
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xxs}, 0.9vw, ${typography.fontSize.sm});
  color: ${colors.highlight.primary};
  border-bottom: 1px solid ${colors.neutral.extraLight};

  &:last-child {
    border-bottom: none;
  }
`;

export const AppendixDataHeader = styled(AppendixDataRow)`
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.highlight.light};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral.light};
  font-size: clamp(8px, 0.8vw, ${typography.fontSize.xxs});
`;

export const AppendixCellHighlight = styled.span<{ $color?: string }>`
  color: ${(p) => p.$color || colors.accent.coral.exxtraDark};
  font-weight: ${typography.fontWeight.bold};
`;

// ============================================
// Card Row
// Responsive flex row for card layouts.
// Wraps on mobile, unwraps at md breakpoint.
// ============================================

export const CardRow = styled.div<{ $gap?: string; $mdGap?: string }>`
  display: flex;
  gap: ${(p) => p.$gap || spacing.lg};
  width: 100%;
  flex-wrap: wrap;

  ${media.md} {
    flex-wrap: nowrap;
    gap: ${(p) => p.$mdGap || p.$gap || spacing.xl};
  }
`;

// ============================================
// Slide Label
// Uppercase colored label for card categories, lesson numbers, etc.
// ============================================

export const SlideLabel = styled.div<{ $color?: string }>`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${(p) => p.$color || colors.highlight.light};
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
`;

// ============================================
// Dark Card Body
// Light-weight body text for dark-background cards.
// ============================================

export const DarkCardBody = styled.p`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xs}, 1.2vw, ${typography.fontSize.sm});
  font-weight: ${typography.fontWeights.light};
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
  line-height: ${typography.lineHeight.relaxed};
`;

// ============================================
// Dark Bullet List
// Bullet list for dark-background cards.
// ============================================

export const DarkBulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

export const DarkBulletItem = styled.li`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xs}, 1.1vw, ${typography.fontSize.sm});
  color: rgba(255, 255, 255, 0.8);
  line-height: ${typography.lineHeight.relaxed};
  padding-left: ${spacing.lg};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${colors.brand.primary};
  }
`;

// ============================================
// Italic Callout Variants
// Styled extensions of AccentCalloutBody for quote blocks.
// ============================================

export const ItalicCalloutBody = styled(AccentCalloutBody)`
  font-style: italic;
`;

export const QuoteAttribution = styled.span`
  font-style: normal;
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.accent.yellow.exxtraDark};
`;
